import axios, { type AxiosError } from 'axios';

import { createHttpError, createHttpRequest } from '@shared/lib/http-request';

import type { Opts } from '../types/common';
import type { HttpError } from '../types/error';
import type { ApiResponse } from '../types/resource';

import { instance } from './config';

/**
 * Преобразует ошибку axios в типизированную HttpError
 */
function toHttpError(e: unknown): HttpError {
  if (axios.isAxiosError(e)) {
    const err = e as AxiosError<{
      message?: string;
      error?: string;
    }>;

    const responseData = err.response?.data;

    const message = responseData?.message ?? responseData?.error ?? err.message;
    const status = err.response?.status;

    return createHttpError(message, status, err.response?.data);
  }

  return new Error('Unknown error');
}

const request = createHttpRequest(instance, toHttpError);

/**
 * HTTP GET запрос
 */
function get<T>(url: string, opts?: Opts): Promise<ApiResponse<T>> {
  return request<ApiResponse<T>>('get', url, undefined, opts);
}

/**
 * HTTP POST запрос
 */
function post<T>(url: string, body?: unknown, opts?: Opts): Promise<ApiResponse<T>> {
  return request<ApiResponse<T>>('post', url, body, opts);
}

/**
 * HTTP PUT запрос
 */
function put<T>(url: string, body?: unknown, opts?: Opts): Promise<ApiResponse<T>> {
  return request<ApiResponse<T>>('put', url, body, opts);
}

/**
 * HTTP PATCH запрос
 */
function patch<T>(url: string, body?: unknown, opts?: Opts): Promise<ApiResponse<T>> {
  return request<ApiResponse<T>>('patch', url, body, opts);
}

/**
 * HTTP DELETE запрос
 */
function del<T>(url: string, opts?: Opts): Promise<ApiResponse<T>> {
  return request<ApiResponse<T>>('delete', url, undefined, opts);
}

/**
 * HTTP-клиент с методами для всех типов запросов
 * Автоматически обрабатывает ошибки и возвращает данные в формате Laravel Resource { data, meta }
 *
 * @example
 * import { http } from '@/shared/api';
 *
 * // GET запрос
 * const response = await http.get<User[]>('/users');
 * console.log(response.data); // User[]
 * console.log(response.meta); // { current_page, total, ... }
 *
 * // POST запрос
 * const response = await http.post<User>('/users', { name: 'John' });
 * console.log(response.data); // User
 *
 * // С параметрами
 * const response = await http.get<User[]>('/users', {
 *   query: { page: 1, limit: 10 }
 * });
 */
export const http = {
  get,
  post,
  put,
  patch,
  delete: del,
  instance,
};
